<template>
  <div class="seo-content">
    <!-- 支持格式部分 -->
    <section class="formats-section">
      <div class="section-header">
        <h2 class="section-title">
          <Icon name="material-symbols:folder-open" size="28" />
          {{ $t('seoContent.formats.title') }}
        </h2>
        <p class="section-subtitle">
          {{ $t('seoContent.formats.subtitle') }}
        </p>
      </div>

      <div class="format-table-container">
        <table class="format-table">
          <thead>
            <tr>
              <th class="icon-column"></th>
              <th class="name-column">{{ $t('seoContent.formats.table.type') }}</th>
              <th class="ext-column">{{ $t('seoContent.formats.table.ext') }}</th>
              <th class="size-column">{{ $t('seoContent.formats.table.size') }}</th>
              <th class="category-column">{{ $t('seoContent.formats.table.category') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in tableRows"
              :key="row.id"
              class="format-row"
            >
              <td class="icon-cell">
                <Icon :name="row.icon" size="28" />
              </td>
              <td class="name-cell">
                <span class="format-name">{{ row.name }}</span>
              </td>
              <td class="ext-cell">
                <code class="format-ext">{{ row.ext }}</code>
              </td>
              <td
                v-if="row.showSizeCell"
                class="size-cell"
                :rowspan="row.sizeRowspan"
              >
                <span class="format-size">{{ $t('seoContent.formats.table.maxSize') }}</span>
              </td>
              <td
                v-if="row.showCategoryCell"
                class="category-cell"
                :rowspan="row.categoryRowspan"
              >
                <div class="category-badge">
                  <Icon :name="row.categoryIcon" size="18" />
                  {{ row.category }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 产品优势部分 -->
    <section class="features-section">
      <div class="section-header">
        <h2 class="section-title">
          <Icon name="material-symbols:star" size="28" />
          {{ $t('seoContent.features.title') }}
        </h2>
        <p class="section-subtitle">
          {{ $t('seoContent.features.subtitle') }}
        </p>
      </div>

      <!-- 翻译流程图 -->
      <div class="process-flow">
        <img
          src="/images/common/translation-process.jpg"
          :alt="$t('seoContent.features.alt.process')"
          class="process-image"
        />
      </div>

      <div class="feature-list">
        <div
          v-for="feature in productFeatures"
          :key="feature.title"
          class="feature-item"
          :class="{ 'has-demo': feature.id === 'layout' || feature.id === 'languages' }"
        >
          <div class="feature-icon">
            <Icon :name="feature.icon" size="48" />
          </div>
          <div class="feature-content">
            <div class="feature-info">
              <h3 class="feature-title">{{ feature.title }}</h3>
              <p class="feature-description">{{ feature.description }}</p>
              <ul class="feature-highlights">
                <li v-for="highlight in feature.highlights" :key="highlight">
                  <Icon name="material-symbols:check-circle" size="16" />
                  {{ highlight }}
                </li>
              </ul>
            </div>
            <!-- 格式保留对比图 -->
            <div v-if="feature.id === 'layout'" class="feature-demo">
              <img
                src="/images/common/format-comparison.jpg"
                :alt="$t('seoContent.features.alt.formatDemo')"
                class="feature-demo-image"
              />
            </div>
            <!-- 多语言支持符号转换 -->
            <div v-if="feature.id === 'languages'" class="feature-demo">
              <img
                src="/images/common/language-symbols.jpg"
                :alt="$t('seoContent.features.alt.langDemo')"
                class="feature-demo-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ部分 -->
    <section class="faq-section">
      <div class="section-header">
        <h2 class="section-title">
          <Icon name="material-symbols:help-center" size="28" />
          {{ $t('seoContent.faq.title') }}
        </h2>
        <p class="section-subtitle">
          {{ $t('seoContent.faq.subtitle') }}
        </p>
      </div>

      <div class="faq-list">
        <div
          v-for="(faq, index) in faqItems"
          :key="index"
          class="faq-item"
        >
          <div
            class="faq-question"
            @click="toggleFaq(index)"
          >
            <h3 class="question-text">{{ faq.question }}</h3>
            <Icon
              name="material-symbols:expand-more"
              size="24"
              class="expand-icon"
              :class="{ rotated: activeFaq === index }"
            />
          </div>
          <div
            class="faq-answer"
            :class="{ expanded: activeFaq === index }"
          >
            <div class="answer-content">
              <p>{{ faq.answer }}</p>
              <div v-if="faq.extra" class="answer-extra">
                <ul>
                  <li v-for="item in faq.extra" :key="item">{{ item }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 使用统计（演示数据） -->
    <section class="stats-section">
      <div class="section-header">
        <h2 class="section-title">
          <Icon name="material-symbols:thumb-up" size="28" />
          {{ $t('seoContent.stats.title') }}
        </h2>
        <p class="section-subtitle">
          {{ $t('seoContent.stats.subtitle') }}
        </p>
      </div>

      <div class="stats-grid">
        <div
          v-for="stat in usageStats"
          :key="stat.label"
          class="stat-item"
        >
          <div class="stat-icon">
            <Icon :name="stat.icon" size="32" />
          </div>
          <div class="stat-number">{{ stat.number }}</div>
          <div class="stat-label">{{ stat.label }}</div>
          <div class="stat-description">{{ stat.description }}</div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
// @nuxt/icon 模块已自动提供 <Icon> 组件，无需手动导入

// FAQ展开状态
const activeFaq = ref<number | null>(null)

// 支持格式数据（i18n）
const supportedFormats = computed(() => [
  {
    name: t('seoContent.formats.categories.office.name'),
    icon: 'material-symbols:work',
    formats: [
      { name: t('seoContent.formats.categories.office.items.word'), ext: '.docx, .doc', icon: 'material-symbols:description', maxSize: '50MB' },
      { name: t('seoContent.formats.categories.office.items.excel'), ext: '.xlsx, .xls', icon: 'material-symbols:table-chart', maxSize: '50MB' },
      { name: t('seoContent.formats.categories.office.items.ppt'), ext: '.pptx, .ppt', icon: 'material-symbols:slideshow', maxSize: '50MB' }
    ]
  },
  {
    name: t('seoContent.formats.categories.pdf.name'),
    icon: 'material-symbols:picture-as-pdf',
    formats: [
      { name: t('seoContent.formats.categories.pdf.items.pdf'), ext: '.pdf', icon: 'material-symbols:picture-as-pdf', maxSize: '50MB' }
    ]
  },
  {
    name: t('seoContent.formats.categories.ebook.name'),
    icon: 'material-symbols:menu-book',
    formats: [
      { name: t('seoContent.formats.categories.ebook.items.epub'), ext: '.epub', icon: 'material-symbols:menu-book', maxSize: '50MB' },
      { name: t('seoContent.formats.categories.ebook.items.mobi'), ext: '.mobi', icon: 'material-symbols:import-contacts', maxSize: '50MB' }
    ]
  },
  {
    name: t('seoContent.formats.categories.subtitle.name'),
    icon: 'material-symbols:subtitles',
    formats: [
      { name: t('seoContent.formats.categories.subtitle.items.srt'), ext: '.srt', icon: 'material-symbols:closed-caption', maxSize: '50MB' },
      { name: t('seoContent.formats.categories.subtitle.items.vtt'), ext: '.vtt', icon: 'material-symbols:closed-caption-outline', maxSize: '50MB' }
    ]
  }
])

// 产品优势数据（i18n）
const productFeatures = computed(() => [
  {
    id: 'layout',
    title: t('seoContent.features.items.layout.title'),
    icon: 'material-symbols:format-paint',
    description: t('seoContent.features.items.layout.description'),
    highlights: [
      t('seoContent.features.items.layout.highlights.0'),
      t('seoContent.features.items.layout.highlights.1'),
      t('seoContent.features.items.layout.highlights.2'),
      t('seoContent.features.items.layout.highlights.3')
    ]
  },
  {
    id: 'languages',
    title: t('seoContent.features.items.languages.title'),
    icon: 'material-symbols:translate',
    description: t('seoContent.features.items.languages.description'),
    highlights: [
      t('seoContent.features.items.languages.highlights.0'),
      t('seoContent.features.items.languages.highlights.1'),
      t('seoContent.features.items.languages.highlights.2'),
      t('seoContent.features.items.languages.highlights.3')
    ]
  },
  {
    id: 'fast',
    title: t('seoContent.features.items.fast.title'),
    icon: 'material-symbols:speed',
    description: t('seoContent.features.items.fast.description'),
    highlights: [
      t('seoContent.features.items.fast.highlights.0'),
      t('seoContent.features.items.fast.highlights.1'),
      t('seoContent.features.items.fast.highlights.2'),
      t('seoContent.features.items.fast.highlights.3')
    ]
  },
  {
    id: 'secure',
    title: t('seoContent.features.items.secure.title'),
    icon: 'material-symbols:security',
    description: t('seoContent.features.items.secure.description'),
    highlights: [
      t('seoContent.features.items.secure.highlights.0'),
      t('seoContent.features.items.secure.highlights.1'),
      t('seoContent.features.items.secure.highlights.2'),
      t('seoContent.features.items.secure.highlights.3')
    ]
  }
])

// i18n
const { t } = useI18n()

// FAQ数据（i18n）
const faqItems = computed(() => [
  {
    question: t('seoContent.faq.items.0.q'),
    answer: t('seoContent.faq.items.0.a'),
    extra: [
      t('seoContent.faq.items.0.extra.0'),
      t('seoContent.faq.items.0.extra.1'),
      t('seoContent.faq.items.0.extra.2'),
      t('seoContent.faq.items.0.extra.3'),
      t('seoContent.faq.items.0.extra.4'),
      t('seoContent.faq.items.0.extra.5')
    ]
  },
  {
    question: t('seoContent.faq.items.1.q'),
    answer: t('seoContent.faq.items.1.a'),
    extra: [
      t('seoContent.faq.items.1.extra.0'),
      t('seoContent.faq.items.1.extra.1'),
      t('seoContent.faq.items.1.extra.2'),
      t('seoContent.faq.items.1.extra.3')
    ]
  },
  {
    question: t('seoContent.faq.items.2.q'),
    answer: t('seoContent.faq.items.2.a'),
    extra: [
      t('seoContent.faq.items.2.extra.0'),
      t('seoContent.faq.items.2.extra.1'),
      t('seoContent.faq.items.2.extra.2'),
      t('seoContent.faq.items.2.extra.3')
    ]
  },
  {
    question: t('seoContent.faq.items.3.q'),
    answer: t('seoContent.faq.items.3.a'),
    extra: [
      t('seoContent.faq.items.3.extra.0'),
      t('seoContent.faq.items.3.extra.1'),
      t('seoContent.faq.items.3.extra.2')
    ]
  },
  {
    question: t('seoContent.faq.items.4.q'),
    answer: t('seoContent.faq.items.4.a'),
    extra: [
      t('seoContent.faq.items.4.extra.0'),
      t('seoContent.faq.items.4.extra.1'),
      t('seoContent.faq.items.4.extra.2'),
      t('seoContent.faq.items.4.extra.3')
    ]
  },
  {
    question: t('seoContent.faq.items.5.q'),
    answer: t('seoContent.faq.items.5.a'),
    extra: [
      t('seoContent.faq.items.5.extra.0'),
      t('seoContent.faq.items.5.extra.1'),
      t('seoContent.faq.items.5.extra.2'),
      t('seoContent.faq.items.5.extra.3')
    ]
  }
])

// 使用统计数据（i18n）
const usageStats = computed(() => [
  { number: '100,000+', label: t('seoContent.stats.items.0.label'), description: t('seoContent.stats.items.0.description'), icon: 'material-symbols:description' },
  { number: '50,000+', label: t('seoContent.stats.items.1.label'), description: t('seoContent.stats.items.1.description'), icon: 'material-symbols:group' },
  { number: '99.9%', label: t('seoContent.stats.items.2.label'), description: t('seoContent.stats.items.2.description'), icon: 'material-symbols:check-circle' },
  { number: '4.9/5', label: t('seoContent.stats.items.3.label'), description: t('seoContent.stats.items.3.description'), icon: 'material-symbols:star-rate' }
])

// 生成带合并单元格的表格数据
const tableRows = computed(() => {
  const rows: Array<{
    id: string
    name: string
    ext: string
    icon: string
    category: string
    categoryIcon: string
    showSizeCell: boolean
    sizeRowspan: number
    showCategoryCell: boolean
    categoryRowspan: number
  }> = []

  let totalFormats = 0
  supportedFormats.value.forEach(category => {
    totalFormats += category.formats.length
  })

  let currentRowIndex = 0
  supportedFormats.value.forEach((category, categoryIndex) => {
    category.formats.forEach((format, formatIndex) => {
      const isFirstInCategory = formatIndex === 0
      const isFirstOverall = currentRowIndex === 0

      rows.push({
        id: `${category.name}-${format.ext}`,
        name: format.name,
        ext: format.ext,
        icon: format.icon,
        category: category.name,
        categoryIcon: category.icon,
        showSizeCell: isFirstOverall, // 只在第一行显示大小单元格
        sizeRowspan: totalFormats, // 跨越所有行
        showCategoryCell: isFirstInCategory, // 只在每个分类的第一行显示分类单元格
        categoryRowspan: category.formats.length // 跨越当前分类的所有行
      })

      currentRowIndex++
    })
  })

  return rows
})

// FAQ切换方法
const toggleFaq = (index: number) => {
  activeFaq.value = activeFaq.value === index ? null : index
}
</script>

<style scoped lang="scss">
.seo-content {
  width: 100%;
  padding: $spacing-xxl 0;
}

// 通用section样式
section {
  margin-bottom: $spacing-xxl;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-header {
  text-align: center;
  margin-bottom: $spacing-xl;

  .section-title {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $spacing-sm;
    font-size: 2rem;
    font-weight: 700;
    color: $gray-900;
    margin: 0 0 $spacing-md 0;
    background: linear-gradient(135deg, $primary-color 0%, $accent-color 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    .iconify {
      color: $accent-color !important;
      filter: drop-shadow(0 2px 4px rgba($accent-color, 0.3));
    }
  }

  .section-subtitle {
    font-size: 1.1rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.6;
  }
}

// 支持格式部分
.formats-section {
  .format-table-container {
    background: rgba($white, 0.88);
    border-radius: $border-radius-lg;
    overflow: hidden;
    box-shadow: $shadow-lg, 0 0 25px rgba($accent-color, 0.15);
    border: 1px solid rgba($accent-color, 0.3);
    backdrop-filter: blur(12px);
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg,
        $accent-color 0%,
        $primary-color 50%,
        $accent-color 100%);
      opacity: 0.7;
      z-index: 1;
    }

    .format-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.9rem;

      thead {
        background: linear-gradient(135deg, $primary-color 0%, $accent-color 100%);

        th {
          padding: $spacing-md $spacing-sm;
          text-align: left;
          font-weight: 600;
          color: $white;
          font-size: 0.85rem;
          letter-spacing: 0.5px;
          border-bottom: 2px solid rgba($white, 0.2);

          &.icon-column {
            width: 60px;
            text-align: center;
          }

          &.name-column {
            width: 25%;
          }

          &.ext-column {
            width: 25%;
          }

          &.size-column {
            width: 15%;
          }

          &.category-column {
            width: 20%;
          }
        }
      }

      tbody {
        .format-row {
          transition: background-color 0.2s ease;
          border-bottom: 1px solid $gray-100;

          &:hover {
            background-color: $gray-50;
          }

          &:last-child {
            border-bottom: none;
          }

          td {
            padding: $spacing-md $spacing-sm;
            vertical-align: middle;

            &.icon-cell {
              text-align: center;
              color: $primary-color;
            }

            &.name-cell {
              .format-name {
                font-weight: 500;
                color: $gray-800;
              }
            }

            &.ext-cell {
              .format-ext {
                background-color: $gray-100;
                color: $primary-color;
                padding: $spacing-xs $spacing-sm;
                border-radius: $border-radius-sm;
                font-family: 'Courier New', Monaco, monospace;
                font-size: 0.8rem;
                font-weight: 500;
              }
            }

            &.size-cell {
              text-align: left;
              vertical-align: middle;
              background-color: rgba($gray-50, 0.5);
              padding-left: $spacing-md;

              .format-size {
                color: $gray-700;
                font-weight: 600;
                font-size: 0.95rem;
              }
            }

            &.category-cell {
              text-align: left;
              vertical-align: middle;
              background-color: rgba($gray-50, 0.3);
              padding-left: $spacing-md;

              .category-badge {
                display: flex;
                align-items: center;
                gap: $spacing-xs;
                color: $gray-600;
                font-size: 0.9rem;
                font-weight: 500;

                .iconify {
                  color: $gray-500;
                }
              }
            }
          }
        }
      }
    }
  }
}

// 产品优势部分
.features-section {
  background: linear-gradient(135deg,
    rgba($white, 0.82) 0%,
    rgba($gray-50, 0.75) 50%,
    rgba($white, 0.82) 100%);
  padding: $spacing-xl;
  border-radius: $border-radius-lg;
  backdrop-filter: blur(8px);
  border: 1px solid rgba($primary-color, 0.2);

  .process-flow {
    text-align: center;
    margin-bottom: $spacing-xl;

    .process-image {
      max-width: 100%;
      height: auto;
      border-radius: $border-radius-lg;
      box-shadow: $shadow-lg, 0 0 20px rgba($primary-color, 0.15);
      transition: transform 0.3s ease, box-shadow 0.3s ease;

      &:hover {
        transform: translateY(-4px);
        box-shadow: $shadow-lg, 0 0 25px rgba($primary-color, 0.2);
      }
    }
  }

  .feature-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: $spacing-xl;

    .feature-item {
      display: flex;
      gap: $spacing-lg;
      padding: $spacing-lg;
      background: rgba($white, 0.85);
      border-radius: $border-radius-lg;
      box-shadow: $shadow-md, 0 0 18px rgba($primary-color, 0.12);
      border: 1px solid rgba($primary-color, 0.15);
      backdrop-filter: blur(5px);

      // 有演示图的功能项特殊样式
      &.has-demo {
        grid-column: 1 / -1; // 跨越整行
        max-width: none;

        .feature-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: $spacing-xl;
          align-items: start;
        }

        .feature-demo {
          grid-column: 2;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .feature-info {
          grid-column: 1;
        }
      }

      .feature-icon {
        flex-shrink: 0;
        color: $primary-color;
      }

      .feature-content {
        .feature-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: $gray-900;
          margin: 0 0 $spacing-sm 0;
        }

        .feature-description {
          font-size: 1rem;
          color: $gray-600;
          margin: 0 0 $spacing-md 0;
          line-height: 1.5;
        }

        .feature-highlights {
          list-style: none;
          padding: 0;
          margin: 0;

          li {
            display: flex;
            align-items: center;
            gap: $spacing-xs;
            margin-bottom: $spacing-xs;
            font-size: 0.9rem;
            color: $gray-700;

            .iconify {
              color: $success;
              flex-shrink: 0;
            }
          }
        }

        .feature-demo {
          .feature-demo-image {
            width: 100%;
            max-width: 500px;
            height: auto;
            border-radius: $border-radius-md;
            box-shadow: $shadow-lg, 0 0 20px rgba($primary-color, 0.15);
            transition: transform 0.3s ease, box-shadow 0.3s ease;

            &:hover {
              transform: translateY(-2px) scale(1.02);
              box-shadow: $shadow-lg, 0 0 25px rgba($primary-color, 0.2);
            }
          }
        }
      }
    }
  }
}

// FAQ部分
.faq-section {
  .faq-list {
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: $spacing-lg;

    .faq-item {
      background: rgba($white, 0.95);
      border-radius: $border-radius-lg;
      box-shadow: $shadow-md, 0 0 20px rgba($accent-color, 0.08);
      border: 1px solid rgba($accent-color, 0.15);
      backdrop-filter: blur(10px);
      overflow: hidden;
      transition: transform 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: $shadow-lg, 0 0 25px rgba($accent-color, 0.15);
      }

      .faq-question {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: $spacing-xl $spacing-lg;
        cursor: pointer;
        background: $white;
        transition: background-color 0.2s ease;
        border-radius: $border-radius-lg $border-radius-lg 0 0;

        &:hover {
          background: linear-gradient(135deg,
            rgba($primary-color, 0.02) 0%,
            rgba($accent-color, 0.02) 100%);
        }

        .question-text {
          font-size: 1.15rem;
          font-weight: 600;
          color: $gray-800;
          margin: 0;
          flex: 1;
          padding-right: $spacing-lg;
          line-height: 1.5;
          word-wrap: break-word;
          display: flex;
          align-items: center;
          min-height: 28px;
        }

        .expand-icon {
          color: $primary-color;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          flex-shrink: 0;
          margin-top: 4px;
          background: rgba($primary-color, 0.1);
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;

          &.rotated {
            transform: rotate(180deg);
            background: rgba($accent-color, 0.15);
            color: $accent-color;
          }
        }
      }

      .faq-answer {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        background: linear-gradient(135deg,
          rgba($gray-50, 0.5) 0%,
          rgba($white, 0.8) 100%);

        &.expanded {
          max-height: 600px;
        }

        .answer-content {
          padding: $spacing-lg $spacing-lg $spacing-xl;

          p {
            font-size: 1.05rem;
            color: $gray-700;
            line-height: 1.7;
            margin: 0 0 $spacing-lg 0;
            font-weight: 400;
          }

          .answer-extra {
            ul {
              list-style: none;
              padding: 0;
              margin: 0;
              background: linear-gradient(135deg,
                rgba($primary-color, 0.03) 0%,
                rgba($accent-color, 0.03) 100%);
              border-radius: $border-radius-md;
              padding: $spacing-lg;
              border: 1px solid rgba($primary-color, 0.1);

              li {
                font-size: 0.95rem;
                color: $gray-700;
                margin-bottom: $spacing-sm;
                padding-left: $spacing-md;
                position: relative;
                line-height: 1.5;
                font-weight: 500;

                &:before {
                  content: '◦';
                  color: $accent-color;
                  position: absolute;
                  left: 0;
                  font-weight: 600;
                  font-size: 1.1rem;
                }

                &:last-child {
                  margin-bottom: 0;
                }
              }
            }
          }
        }
      }
    }
  }
}

// 使用统计部分
.stats-section {
  background: linear-gradient(135deg, $primary-color 0%, $accent-color 100%);
  padding: $spacing-xxl $spacing-xl;
  border-radius: $border-radius-lg;
  color: $white;
  position: relative;
  overflow: hidden;
  box-shadow: $shadow-lg, 0 6px 30px rgba($accent-color, 0.18);

  // subtle light overlay to increase readability
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(1200px 400px at 20% -10%, rgba($white, 0.15), transparent 60%),
                radial-gradient(1000px 500px at 120% 110%, rgba($white, 0.12), transparent 60%);
    pointer-events: none;
  }

  .section-header {
    .section-title {
      color: $white;
      background: none; // override global gradient text
      -webkit-text-fill-color: $white; // ensure solid white on webkit
      -webkit-background-clip: initial;
      background-clip: initial;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.24);

      .iconify {
        color: $white !important; // ensure icon contrasts on gradient
        filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.25));
      }
    }

    .section-subtitle {
      color: $white;
      opacity: 0.95;
    }
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: $spacing-xl;

    .stat-item {
      text-align: center;
      padding: $spacing-xl $spacing-lg;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: $spacing-sm;
      background: rgba($white, 0.12);
      border: 1px solid rgba($white, 0.28);
      border-radius: $border-radius-lg;
      backdrop-filter: blur(6px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
      transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
        background: rgba($white, 0.16);
      }

      .stat-icon {
        color: $white;
        margin-bottom: $spacing-xs;
        filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.25));
      }

      .stat-number {
        font-size: 2.75rem;
        font-weight: 800;
        letter-spacing: 0.3px;
        margin-bottom: $spacing-xs;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
      }

      .stat-label {
        font-size: 1.05rem;
        font-weight: 600;
        opacity: 0.95;
        margin-bottom: $spacing-xs;
      }

      .stat-description {
        font-size: 0.9rem;
        opacity: 0.9;
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .seo-content {
    padding: $spacing-lg 0;
  }

  .section-header {
    .section-title {
      font-size: 1.5rem;
    }

    .section-subtitle {
      font-size: 1rem;
    }
  }

  .format-table-container {
    .format-table {
      font-size: 0.8rem;

      thead th {
        padding: $spacing-sm;
        font-size: 0.75rem;

        &.icon-column {
          width: 40px;
        }
      }

      tbody td {
        padding: $spacing-sm;

        &.ext-cell .format-ext {
          font-size: 0.7rem;
          padding: 2px $spacing-xs;
        }

        &.size-cell .format-size {
          font-size: 0.85rem;
        }

        &.category-cell .category-badge {
          font-size: 0.75rem;
        }
      }
    }
  }

  .feature-list {
    grid-template-columns: 1fr;

    .feature-item {
      flex-direction: column;
      text-align: center;
    }
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);

    .stat-item {
      padding: $spacing-lg;
      .stat-number { font-size: 2.2rem; }
      .stat-label { font-size: 1rem; }
    }
  }

  .faq-list {
    max-width: 100%;
    gap: $spacing-md;

    .faq-item {
      .faq-question {
        padding: $spacing-lg $spacing-md;
        align-items: center;

        .question-text {
          font-size: 1.05rem;
          min-height: auto;
          padding-right: $spacing-md;
        }

        .expand-icon {
          width: 28px;
          height: 28px;
          margin-top: 0;
        }
      }

      .faq-answer {
        .answer-content {
          padding: $spacing-md;

          p {
            font-size: 1rem;
          }

          .answer-extra ul {
            padding: $spacing-md;

            li {
              font-size: 0.9rem;
            }
          }
        }
      }
    }
  }
}

// 平板端适配
@media (min-width: 769px) and (max-width: 1024px) {
  .format-table-container {
    .format-table {
      font-size: 0.85rem;
    }
  }

  .feature-list {
    grid-template-columns: 1fr;
  }
}

// 滚动动画
section {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

</style>
